/**
 * UrbanEntityList — Lista de entidades del inventario urbano
 *
 * @remarks
 * Muestra la lista de urban_entities con tipo, estado, importBatchId corto
 * y atributo principal (si existe). Click selecciona la entidad.
 *
 * @module urban-inventory/components
 */

import { MapPin } from 'lucide-react';
import { useUrbanInventoryStore } from '../store/urban-inventory-store';
import type { UrbanEntity } from '../types/urban-entity.types';
import { colorForType } from '../utils/urban-entity-colors';

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  removed: 'Removido',
  pending_review: 'En revisión',
};

const TYPE_LABELS: Record<string, string> = {
  tree: 'Árbol',
  streetlight: 'Luminaria',
  waste_container: 'Contenedor',
  transit_stop: 'Parada',
  bench: 'Banco',
  traffic_light: 'Semáforo',
  fountain: 'Fuente',
  waste_depot: 'Depósito',
  waste_hotspot: 'Microbasural',
  building_centroid: 'Edificio',
};

function labelForType(entityType: string): string {
  return TYPE_LABELS[entityType] ?? entityType;
}

/** Extrae el primer atributo de texto/número del JSONB para mostrarlo como resumen */
function primaryAttribute(attributes: Record<string, unknown>): string | null {
  const entries = Object.entries(attributes);
  if (entries.length === 0) return null;
  const [key, value] = entries[0];
  if (value === null || value === undefined) return null;
  return `${key}: ${String(value)}`;
}

interface UrbanEntityListProps {
  entities: UrbanEntity[];
  isLoading: boolean;
  error: string | null;
}

export function UrbanEntityList({ entities, isLoading, error }: UrbanEntityListProps): JSX.Element {
  const selectedEntityId = useUrbanInventoryStore((s) => s.selectedEntityId);
  const selectEntity = useUrbanInventoryStore((s) => s.selectEntity);
  const setHoveredEntity = useUrbanInventoryStore((s) => s.setHoveredEntity);

  if (isLoading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: 'var(--color-text-muted, #94a3b8)', fontSize: '12px' }}>
        Cargando entidades...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '12px', color: '#ef4444', fontSize: '12px', background: '#fef2f2', borderRadius: '4px', margin: '8px' }}>
        Error: {error}
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--color-text-muted, #94a3b8)' }}>
        <MapPin size={24} style={{ marginBottom: '8px', opacity: 0.4 }} />
        <p style={{ margin: 0, fontSize: '13px' }}>Sin entidades en el inventario</p>
        <p style={{ margin: '4px 0 0', fontSize: '11px' }}>
          Importa entidades desde la Fase 1 para verlas aquí.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'auto', flex: 1 }}>
      {entities.map((entity) => {
        const isSelected = entity.id === selectedEntityId;
        const color = colorForType(entity.entityType);
        const attrSummary = primaryAttribute(entity.attributes);
        const batchShort = entity.importBatchId
          ? entity.importBatchId.substring(0, 8)
          : null;

        return (
          <button
            key={entity.id}
            type="button"
            onClick={() => selectEntity(entity)}
            onMouseEnter={() => setHoveredEntity(entity.id)}
            onMouseLeave={() => setHoveredEntity(null)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '8px 12px',
              border: 'none',
              borderBottom: '1px solid var(--color-border-subtle, #f1f5f9)',
              background: isSelected
                ? 'var(--color-accent-subtle, #eff6ff)'
                : 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
              transition: 'background 0.1s',
            }}
          >
            {/* Color dot */}
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: color,
                flexShrink: 0,
                marginTop: '4px',
              }}
            />

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Tipo + estado */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '4px' }}>
                <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-text, #1e293b)' }}>
                  {labelForType(entity.entityType)}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    color: entity.status === 'active'
                      ? '#22c55e'
                      : entity.status === 'removed'
                        ? '#ef4444'
                        : '#94a3b8',
                    flexShrink: 0,
                  }}
                >
                  {STATUS_LABELS[entity.status] ?? entity.status}
                </span>
              </div>

              {/* Tipo original si fue reclasificado */}
              {entity.originalEntityType && (
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>
                  Antes: {entity.originalEntityType}
                </div>
              )}

              {/* Atributo principal */}
              {attrSummary && (
                <div
                  style={{ fontSize: '11px', color: 'var(--color-text-secondary, #64748b)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {attrSummary}
                </div>
              )}

              {/* Batch ID corto */}
              {batchShort && (
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>
                  lote: {batchShort}…
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
